import { getAppLanguage, toLocale } from "./appLanguage";
// utils/regions.ts
//
// Single source of truth for the countries/regions Astria supports worldwide.
//
// The region *values* here must match the ones stored on user.region and listed
// in REGION_OPTIONS (auth/Register.tsx) — they are what the backend sends and
// what the sidebar filters categories by. This module adds the presentation
// layer on top: flag, display label, continent grouping and a short tagline.

export const ALL_REGIONS_VALUE = "all";

export type Continent = "Asia" | "Middle East" | "Europe" | "Americas";

export interface WorldRegion {
  /** Region code stored on user.region (e.g. "healjai" for Thailand). */
  value: string;
  /** Display name, e.g. "Thailand". */
  label: string;
  continent: Continent;
  /** Short line describing the local astrology system Astria uses there. */
  tagline: string;
  /** ISO 4217 currency code Stripe should charge in for this region. */
  currency: string;
  /** Symbol/prefix shown before the amount, e.g. "¥", "RM", "Rp". */
  currencySymbol: string;
  /** Monthly price, in the region's currency (major units, e.g. dollars not cents). */
  monthlyPrice: number;
  /** Annual price, in the region's currency. */
  annualPrice: number;
}

// Ordered by continent so the grid reads as a journey around the globe.
export const WORLD_REGIONS: WorldRegion[] = [
  // ── Asia ──────────────────────────────────────────────────────────────
  {
    value: "healjai",
    label: "Thailand",
    continent: "Asia",
    tagline: "Thai healing horoscope",
    currency: "THB",
    currencySymbol: "฿",
    monthlyPrice: 149,
    annualPrice: 1490,
  },
  {
    value: "japan",
    label: "Japan",
    continent: "Asia",
    tagline: "Kyusei nine-star ki",
    currency: "JPY",
    currencySymbol: "¥",
    monthlyPrice: 450,
    annualPrice: 4500,
  },
  {
    value: "korea",
    label: "Korea",
    continent: "Asia",
    tagline: "Saju four pillars",
    currency: "KRW",
    currencySymbol: "₩",
    monthlyPrice: 4900,
    annualPrice: 49000,
  },
  {
    value: "china",
    label: "China",
    continent: "Asia",
    tagline: "Bazi four pillars",
    currency: "CNY",
    currencySymbol: "¥",
    monthlyPrice: 18,
    annualPrice: 180,
  },
  {
    value: "india",
    label: "India",
    continent: "Asia",
    tagline: "Vedic jyotish",
    currency: "INR",
    currencySymbol: "₹",
    monthlyPrice: 149,
    annualPrice: 1490,
  },
  {
    value: "indonesia",
    label: "Indonesia",
    continent: "Asia",
    tagline: "Javanese primbon",
    currency: "IDR",
    currencySymbol: "Rp",
    monthlyPrice: 39000,
    annualPrice: 390000,
  },
  {
    value: "malaysia",
    label: "Malaysia",
    continent: "Asia",
    tagline: "Blended eastern reading",
    currency: "MYR",
    currencySymbol: "RM",
    monthlyPrice: 12,
    annualPrice: 120,
  },
  {
    value: "singapore",
    label: "Singapore",
    continent: "Asia",
    tagline: "Bazi and feng shui",
    currency: "SGD",
    currencySymbol: "$",
    monthlyPrice: 4.99,
    annualPrice: 49.99,
  },
  {
    value: "philippines",
    label: "Philippines",
    continent: "Asia",
    tagline: "Warm guidance readings",
    currency: "PHP",
    currencySymbol: "₱",
    monthlyPrice: 149,
    annualPrice: 1490,
  },
  {
    value: "vietnam",
    label: "Vietnam",
    continent: "Asia",
    tagline: "Tu vi birth chart",
    currency: "VND",
    currencySymbol: "đ",
    monthlyPrice: 99000,
    annualPrice: 990000,
  },

  // ── Middle East ───────────────────────────────────────────────────────
  {
    value: "gcc",
    label: "GCC",
    continent: "Middle East",
    tagline: "Arabic-language companion",
    currency: "USD",
    currencySymbol: "$",
    monthlyPrice: 5.99,
    annualPrice: 59.99,
  },

  // ── Europe ────────────────────────────────────────────────────────────
  {
    value: "uk",
    label: "United Kingdom",
    continent: "Europe",
    tagline: "Western zodiac",
    currency: "GBP",
    currencySymbol: "£",
    monthlyPrice: 7.99,
    annualPrice: 79.99,
  },
  {
    value: "spanish",
    label: "Spanish",
    continent: "Europe",
    tagline: "Spanish-language readings",
    currency: "EUR",
    currencySymbol: "€",
    monthlyPrice: 4.99,
    annualPrice: 49.99,
  },
  {
    value: "italy",
    label: "Italy",
    continent: "Europe",
    tagline: "Oroscopo e astrologia",
    currency: "EUR",
    currencySymbol: "€",
    monthlyPrice: 4.99,
    annualPrice: 49.99,
  },
  {
    value: "germany",
    label: "Germany",
    continent: "Europe",
    tagline: "Horoskop und Astrologie",
    currency: "EUR",
    currencySymbol: "€",
    monthlyPrice: 4.99,
    annualPrice: 49.99,
  },
  {
    value: "france",
    label: "France",
    continent: "Europe",
    tagline: "Horoscope et astrologie",
    currency: "EUR",
    currencySymbol: "€",
    monthlyPrice: 4.99,
    annualPrice: 49.99,
  },
  {
    value: "russia",
    label: "Russia",
    continent: "Europe",
    tagline: "Гороскоп и астрология",
    currency: "RUB",
    currencySymbol: "₽",
    monthlyPrice: 399,
    annualPrice: 3990,
  },

  // ── Americas ──────────────────────────────────────────────────────────
  {
    value: "us",
    label: "United States",
    continent: "Americas",
    tagline: "Modern western astrology",
    currency: "USD",
    currencySymbol: "$",
    monthlyPrice: 7.99,
    annualPrice: 79.99,
  },
  {
    value: "canada",
    label: "Canada",
    continent: "Americas",
    tagline: "Western zodiac",
    currency: "CAD",
    currencySymbol: "$",
    monthlyPrice: 6.99,
    annualPrice: 69.99,
  },
  {
    value: "mexico",
    label: "Mexico",
    continent: "Americas",
    tagline: "Astrologia y tarot",
    currency: "MXN",
    currencySymbol: "$",
    monthlyPrice: 79,
    annualPrice: 790,
  },
  {
    value: "brazil",
    label: "Brazil",
    continent: "Americas",
    tagline: "Mapa astral",
    currency: "BRL",
    currencySymbol: "$",
    monthlyPrice: 14,
    annualPrice: 140,
  },
];

/** Continents in the order the Worldwide grid renders them. */
export const CONTINENT_ORDER: Continent[] = [
  "Asia",
  "Middle East",
  "Europe",
  "Americas",
];

const REGION_BY_VALUE: Record<string, WorldRegion> = WORLD_REGIONS.reduce(
  (acc, region) => {
    acc[region.value] = region;
    return acc;
  },
  {} as Record<string, WorldRegion>,
);

/**
 * Look up a region by the value stored on user.region. "thailand" is accepted
 * as an alias for "healjai" the same way branding.ts accepts it.
 */
export const getRegion = (value?: string | null): WorldRegion | undefined => {
  const key = (value || "").trim().toLowerCase();
  if (!key) return undefined;
  if (key === "thailand") return REGION_BY_VALUE["healjai"];
  return REGION_BY_VALUE[key];
};

/** Display label for a region value, falling back to the raw value. */
export const getRegionLabel = (value?: string | null): string => {
  if ((value || "").trim().toLowerCase() === ALL_REGIONS_VALUE) {
    return "All Regions";
  }
  return getRegion(value)?.label || value || "";
};

/** True when `value` is the same region as `current` (alias-aware). */
export const isSameRegion = (
  value?: string | null,
  current?: string | null,
): boolean => {
  const a = getRegion(value);
  const b = getRegion(current);
  return !!a && !!b && a.value === b.value;
};

/** Regions grouped by continent, in CONTINENT_ORDER, skipping empty groups. */
export const getRegionsByContinent = (): {
  continent: Continent;
  regions: WorldRegion[];
}[] =>
  CONTINENT_ORDER.map((continent) => ({
    continent,
    regions: WORLD_REGIONS.filter((r) => r.continent === continent),
  })).filter((group) => group.regions.length > 0);

// ── Regional accent colors ────────────────────────────────────────────────
//
// Drawn from the aurora gradient behind the Sanctuary hero (pink → violet →
// blue → cyan → warm amber) so the Worldwide grid feels like part of the same
// cosmos rather than a flat wall of one hue. Each continent gets its own band
// of that spectrum, which also makes the grouping readable at a glance.

export interface ContinentAccent {
  /** Solid accent for text and icons. */
  base: string;
  /** Soft fill behind a region badge. */
  soft: string;
  /** Border tint for the badge. */
  ring: string;
}

export const CONTINENT_ACCENTS: Record<Continent, ContinentAccent> = {
  Asia: {
    base: "#C2569B",
    soft: "linear-gradient(150deg, #FFF2F9 0%, #FCE4F1 100%)",
    ring: "rgba(194,86,155,0.28)",
  },
  "Middle East": {
    base: "#C9862F",
    soft: "linear-gradient(150deg, #FFF8EC 0%, #FCEBD2 100%)",
    ring: "rgba(201,134,47,0.30)",
  },
  Europe: {
    base: "#6D4C92",
    soft: "linear-gradient(150deg, #F6F1FF 0%, #EADFFB 100%)",
    ring: "rgba(109,76,146,0.28)",
  },
  Americas: {
    base: "#2F7FB8",
    soft: "linear-gradient(150deg, #EEF7FF 0%, #DCEDFB 100%)",
    ring: "rgba(47,127,184,0.28)",
  },
};

/** Accent palette for a region, by its continent. */
export const getRegionAccent = (region: WorldRegion): ContinentAccent =>
  CONTINENT_ACCENTS[region.continent];

// ── Pricing formatting ──────────────────────────────────────────────────────

/** Currencies conventionally shown with no decimal places (e.g. ¥450, not ¥450.00). */
const ZERO_DECIMAL_CURRENCIES = new Set(["JPY", "KRW", "IDR", "VND"]);

/**
 * Formats a region's price for display, e.g. "$7.99" or "¥450". Falls back to
 * two decimals unless the currency is conventionally shown as a whole number.
 */
export const formatRegionPrice = (
  region: WorldRegion,
  amount: number,
): string => {
  const showDecimals = !ZERO_DECIMAL_CURRENCIES.has(region.currency);
  const formatted = showDecimals
    ? amount.toLocaleString(toLocale(getAppLanguage()), {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : Math.round(amount).toLocaleString(toLocale(getAppLanguage()));
  return `${region.currencySymbol}${formatted}`;
};
